import { FunctionComponent, useState } from 'react';
import { faEdit, faBan, faCopy, faChevronRight, faChevronDown } from '@fortawesome/pro-regular-svg-icons';
import { TableRow } from '../../UI/V3/Table/Row/TableRow';
import { TableCell } from '../../UI/V3/Table/Cell/TableCell';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useOrganizationsContext } from '../OrganizationsProvider';
import { Organization } from 'model/organization';
import { ActionsDropdown } from '../../common/ActionsDropdown/ActionsDropdown';
import { ActionsDropdownOption } from '../../common/ActionsDropdown/ActionsDropdownOption';
import styles from './OrganizationRow.module.scss';

interface OrganizationRowProps {
    account: Organization;
    allChildAccounts: Organization[];
    isLast: boolean;
    isMobile: boolean;
    depth: number;
}

const OrganizationRow: FunctionComponent<OrganizationRowProps> = ({
    account,
    allChildAccounts,
    isLast,
    isMobile,
    depth
}) => {
    const { handleEditAccount, handleToggleStatus } = useOrganizationsContext();

    // Use smaller padding multiplier on mobile to prevent overflow at deeper nesting
    const paddingMultiplier = isMobile ? 12 : 24;

    // Get immediate children of this account
    const childAccounts = allChildAccounts.filter(child => child.parentOrganizationId === account.id);

    const hasChildren = childAccounts.length > 0;
    const [isExpanded, setIsExpanded] = useState(false);

    const toggleExpanded = () => {
        if (hasChildren) {
            setIsExpanded(!isExpanded);
        }
    };

    const editOption: ActionsDropdownOption = {
        id: 'edit',
        label: 'Edit',
        icon: faEdit,
        onClick: () => handleEditAccount(account)
    };

    const deactivateOption: ActionsDropdownOption = {
        id: 'deactivate',
        label: account.active ? 'Deactivate' : 'Activate',
        icon: faBan,
        onClick: () => handleToggleStatus('deactivate', account)
    };

    const activateOption = {
        id: 'activate',
        label: 'Reactivate',
        icon: faCopy,
        onClick: () => handleToggleStatus('activate', account)
    };

    // Child accounts only show Edit, Add Location, Delete
    // Parent accounts show Edit, Activate/Deactivate, Delete
    const options = account.active ? [editOption] : [editOption];

    return (
        <>
            <TableRow
                className={`${styles.desktopRow} ${!account.active ? styles.inactiveRow : ''} ${
                    isExpanded && hasChildren ? styles.expandedRow : ''
                }`}
                style={isMobile ? { paddingLeft: `${depth * paddingMultiplier}px` } : {}}
            >
                <TableCell className={styles.nameCell}>
                    <div
                        className={styles.nameCellContent}
                        style={
                            !isMobile
                                ? { paddingLeft: `${depth * paddingMultiplier}px` }
                                : { marginLeft: `${depth * paddingMultiplier}px` }
                        }
                    >
                        {hasChildren ? (
                            <div className={styles.expandIconWrapper}>
                                <FontAwesomeIcon
                                    icon={isExpanded ? faChevronDown : faChevronRight}
                                    className={styles.expandIcon}
                                    onClick={toggleExpanded}
                                />
                            </div>
                        ) : (
                            <div style={{ width: '15px' }} />
                        )}
                        <div className={styles.nameContent}>
                            <span className={styles.accountName}>{account.name}</span>
                        </div>
                    </div>
                </TableCell>

                <TableCell>
                    <span className={styles.type}>
                        {account.businessType
                            ? account.businessType[0].toUpperCase() + account.businessType.slice(1)
                            : ''}{' '}
                    </span>
                </TableCell>

                <TableCell>
                    <span className={styles.cityState}>
                        {account.city}, {account.state}
                    </span>
                </TableCell>

                <TableCell>
                    <span className={styles.accountsCount}>{account.marketplacesCount}</span>
                </TableCell>

                <TableCell isActions>
                    <div className={styles.actionsWrapper}>
                        <ActionsDropdown
                            options={options}
                            direction={isLast ? 'up' : 'down'}
                        />
                    </div>
                </TableCell>
            </TableRow>

            {isExpanded &&
                hasChildren &&
                childAccounts.map((child, index) => (
                    <OrganizationRow
                        key={child.id}
                        account={child}
                        allChildAccounts={allChildAccounts}
                        isLast={index === childAccounts.length - 1}
                        isMobile={isMobile}
                        depth={depth + 1}
                    />
                ))}
        </>
    );
};

export default OrganizationRow;
